'use client';

import React from 'react';
import MarketingLayout from '@/components/MarketingLayout';

export default function ContactPage() {
  return (
    <MarketingLayout>
      {/* Hero */}
      <section className="relative pt-32 pb-16 lg:pt-40 lg:pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-[#0A0F1A]" />
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(ellipse 60% 50% at 50% 0%, rgba(249,115,22,0.06) 0%, transparent 60%)',
        }} />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <span className="text-[#F97316] text-xs font-semibold uppercase tracking-widest">Contact</span>
          <h1 className="text-display text-[#F1F5F9] mt-4 mb-4 max-w-2xl mx-auto">
            Talk to the BridgeFlow™ team
          </h1>
          <p className="text-[#64748B] text-lg max-w-xl mx-auto">
            Tell us about your operation. We will show you exactly how BridgeFlow™ MTE fits your shutdown schedule and what the numbers look like.
          </p>
        </div>
      </section>
      {/* Content */}
      <section className="pb-24 lg:pb-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            {/* Left — info */}
            <div className="lg:col-span-4 space-y-8">
              {/* What to expect */}
              <div className="rounded-2xl border border-[#1E293B] bg-[#111827] p-7">
                <h3 className="text-[#F1F5F9] font-bold text-lg mb-5">What happens next</h3>
                <div className="space-y-5">
                  {[
                    { step: '01', title: 'We review your scope', desc: 'Tell us your flange count, downtime cost, and shutdown schedule.' },
                    { step: '02', title: 'Custom ROI model', desc: 'We build a site-specific ROI analysis for your operation.' },
                    { step: '03', title: 'Product demonstration', desc: 'See BridgeFlow™ MTE in action — live or via detailed walkthrough.' },
                    { step: '04', title: 'Commercial discussion', desc: 'We discuss the right version, deployment timeline, and commercial terms.' },
                  ]?.map((s) => (
                    <div key={s?.step} className="flex gap-4">
                      <div className="font-mono text-[#F97316]/40 text-2xl font-bold flex-shrink-0 w-8">{s?.step}</div>
                      <div>
                        <p className="text-[#F1F5F9] font-semibold text-sm">{s?.title}</p>
                        <p className="text-[#64748B] text-sm mt-0.5">{s?.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Key stats */}
              <div className="rounded-2xl border border-[#F97316]/20 bg-gradient-to-br from-[#111827] to-[#0F172A] p-7">
                <p className="text-[#F97316] text-xs font-semibold uppercase tracking-widest mb-4">Why act now</p>
                <div className="space-y-4">
                  {[
                    { value: '~105%', label: 'First-event ROI at typical downtime costs' },
                    { value: '<1', label: 'Shutdown cycles to full payback' },
                    { value: '90%', label: 'Reduction in flange assembly time' },
                    { value: '$511K', label: 'Savings per event (40 flanges, $20K/hr downtime)' },
                  ]?.map((s) => (
                    <div key={s?.label} className="flex items-center gap-4">
                      <div className="font-mono text-[#F97316] font-bold text-xl w-20 flex-shrink-0">{s?.value}</div>
                      <p className="text-[#64748B] text-sm">{s?.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right — email CTA */}
            <div className="lg:col-span-8 flex items-center justify-center">
              <div className="rounded-2xl border border-[#1E293B] bg-[#111827] p-12 text-center w-full">
                <div className="w-16 h-16 rounded-full bg-[#F97316]/10 border border-[#F97316]/30 flex items-center justify-center mx-auto mb-6">
                  <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M4 7a2 2 0 012-2h16a2 2 0 012 2v14a2 2 0 01-2 2H6a2 2 0 01-2-2V7z" stroke="#F97316" strokeWidth="1.5"/>
                    <path d="M4 8l10 7 10-7" stroke="#F97316" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <h3 className="text-[#F1F5F9] font-bold text-2xl mb-3">Send us an inquiry</h3>
                <p className="text-[#64748B] text-lg max-w-md mx-auto mb-8">
                  Ready to learn more? Send us an email and a member of the BridgeFlow™ team will be in touch within one business day.
                </p>
                <a
                  href="mailto:info@irongatesupply.com?subject=Bridgeflow%20Valve%20MTE%20inquiry"
                  className="inline-flex items-center gap-3 px-8 py-4 rounded-xl bg-[#F97316] hover:bg-[#EA6C0A] text-white font-semibold text-base transition-colors duration-150"
                >
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M2.5 5a1.5 1.5 0 011.5-1.5h12A1.5 1.5 0 0117.5 5v10a1.5 1.5 0 01-1.5 1.5H4A1.5 1.5 0 012.5 15V5z" stroke="white" strokeWidth="1.25"/>
                    <path d="M2.5 5.5l7.5 5.25L17.5 5.5" stroke="white" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  info@irongatesupply.com
                </a>
                <p className="text-[#475569] text-sm mt-5">
                  Subject: <span className="text-[#94A3B8]">Bridgeflow Valve MTE inquiry</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
